import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import {
  HOW_RESULTS_LABELS, Invitation, InvitationStatus, MessageKey, Organizer, OrganizerRole, Participant, ROLE_LABELS,
  RosterRef, STATUS_LABELS, WHO_INVITES_LABELS, WHO_PAYS_LABELS,
} from '../models/invitation.model';
import { MESSAGE_TEMPLATES } from '../data/invitations.seed';
import { FsReportMember, FsReportTarget } from '../../common/fs-report-download/fs-report-download.component';
import { FsComboOption } from '../../common/fs-combobox/fs-combobox.component';
import { Session, SPLIT_TYPE_LABELS } from '../../sessions/models/session.model';
import { SessionMockService } from '../../sessions/services/session-mock.service';

type PSortCol = 'firstName' | 'lastName' | 'email' | 'profile' | 'cl' | 'id' | 'dv' | 'im' | 'completed';
import { InvitationMockService } from '../services/invitation-mock.service';

declare var $: any;

@Component({
  selector: 'app-invitation-detail',
  templateUrl: './invitation-detail.component.html',
  styleUrls: ['./invitation-detail.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class InvitationDetailComponent implements OnInit {
  inv?: Invitation;
  statuses: InvitationStatus[] = ['open', 'closed'];
  statusLabels = STATUS_LABELS;
  roleLabels = ROLE_LABELS;
  showDetails = false;
  showEditModal = false;

  // "Send Participant Email" modal
  showEmailModal = false;
  emailParticipant: Participant | null = null;

  // Combined "Download" dropdown
  dlOpen = false;

  // scoring-keys transfer modal
  rosters: RosterRef[] = [];
  showKeysModal = false;
  keysCount: number | null = null;
  keysFrom: string | null = null;

  // unlock-profiles modal
  showUnlockModal = false;

  // participants data grid
  pSearch = '';
  pStage: 'all' | 'scored' | 'locked' | 'started' | 'not_started' = 'all';
  pSortCol: PSortCol = 'lastName';
  pSortDir: 'asc' | 'desc' = 'asc';
  pPage = 0;
  // Most invitations fit on one page at 50, so the default avoids paging for the common case.
  pPageSize = 50;
  pPageSizes = [10, 20, 50, 100];
  pSelected = new Set<string>();

  setStage(s: 'all' | 'scored' | 'locked' | 'started' | 'not_started'): void {
    this.pStage = s; this.pPage = 0; this.pSelected.clear();
  }

  refreshing = false;
  /** Reload the invitation's participants from the (mock) service. */
  refreshParticipants(): void {
    if (!this.inv || this.refreshing) { return; }
    this.refreshing = true;
    this.svc.getById(this.inv.id).subscribe(u => { if (u) { this.inv = u; } });
    this.pSelected.clear();
    setTimeout(() => { this.refreshing = false; }, 500);
  }
  pFiltered(): Participant[] {
    let rows = this.inv?.participants ?? [];
    if (this.pStage !== 'all') { rows = rows.filter(p => this.stageKey(p) === this.pStage); }
    if (this.pSearch) {
      const s = this.pSearch.toLowerCase();
      rows = rows.filter(p => (p.firstName + ' ' + p.lastName).toLowerCase().includes(s) || p.email.toLowerCase().includes(s));
    }
    const dir = this.pSortDir === 'asc' ? 1 : -1;
    const val = (p: Participant): string | number => {
      switch (this.pSortCol) {
        case 'firstName': return p.firstName.toLowerCase();
        case 'email': return p.email.toLowerCase();
        case 'profile': return p.profileType ?? '';
        case 'completed': return p.lastActivity ?? '';
        case 'cl': case 'id': case 'dv': case 'im': return p.scores ? p.scores[this.pSortCol] : -1;
        default: return p.lastName.toLowerCase();   // 'lastName'
      }
    };
    return [...rows].sort((a, b) => { const av = val(a), bv = val(b); return av < bv ? -dir : av > bv ? dir : 0; });
  }
  pTotal(): number { return this.pFiltered().length; }
  pPaged(): Participant[] { const start = this.pPage * this.pPageSize; return this.pFiltered().slice(start, start + this.pPageSize); }
  pPageCount(): number { return Math.max(1, Math.ceil(this.pTotal() / this.pPageSize)); }
  pPages(): number[] { return Array.from({ length: this.pPageCount() }, (_, i) => i); }
  pStart(): number { return this.pTotal() === 0 ? 0 : this.pPage * this.pPageSize + 1; }
  pEnd(): number { return Math.min((this.pPage + 1) * this.pPageSize, this.pTotal()); }

  pSetSort(col: PSortCol): void {
    if (this.pSortCol === col) { this.pSortDir = this.pSortDir === 'asc' ? 'desc' : 'asc'; }
    else { this.pSortCol = col; this.pSortDir = 'asc'; }
    this.pPage = 0;
  }
  pSortIcon(col: PSortCol): string { return this.pSortCol !== col ? 'fa-sort' : (this.pSortDir === 'asc' ? 'fa-sort-asc' : 'fa-sort-desc'); }
  pSetPage(p: number): void { this.pPage = Math.max(0, Math.min(p, this.pPageCount() - 1)); }
  pOnSearch(): void { this.pPage = 0; }
  barH(score: number): number { return Math.max(3, Math.min(24, Math.round((score / 40) * 24))); }
  /** Open the App-style "Send Participant Email" modal — a fixed results-email template filled with this participant's data. */
  sendParticipantEmail(p: Participant): void {
    this.emailParticipant = p;
    this.showEmailModal = true;
  }
  closeEmailModal(): void { this.showEmailModal = false; this.emailParticipant = null; }

  get emailOwner(): string { return this.inv?.organizers[0]?.name ?? 'the FourSight team'; }

  /** Result links for the email template (mock URLs until the API provides per-participant share links). */
  emailLinks(p: Participant): { label: string; url: string }[] {
    const base = 'https://app.foursightonline.com/share';
    return [
      { label: 'Open FourSight Thinking Profile Slideshow', url: `${base}/slideshow/${p.id}` },
      { label: 'Download my profile', url: `${base}/profile/${p.id}` },
      { label: 'Download a short interpretive guide', url: `${base}/guide/${p.id}` },
    ];
  }

  /** Plain-text version of the email body, used for Copy and mailto (links rendered as URLs). */
  private emailPlainBody(p: Participant): string {
    const name = `${p.firstName} ${p.lastName}`.trim();
    const links = this.emailLinks(p).map(l => `- ${l.label}: ${l.url}`).join('\n');
    return [
      `Dear ${name},`, '',
      'Thanks for taking the FourSight assessment.', '',
      'For your records, here are your results:',
      links, '',
      'If you have trouble opening any of these items, be sure your adblocker is paused.', '',
      'Best,', this.emailOwner,
    ].join('\n');
  }

  copyEmailContent(): void {
    const p = this.emailParticipant; if (!p) return;
    const text = `Subject: Your FourSight Thinking Profile\n\n${this.emailPlainBody(p)}`;
    navigator.clipboard?.writeText(text).then(
      () => Swal.fire({ title: 'Copied to clipboard', icon: 'success', timer: 1100, showConfirmButton: false }),
      () => Swal.fire({ title: 'Copy failed', text: 'Select and copy the text manually.', icon: 'error', timer: 1500, showConfirmButton: false }),
    );
  }

  mailtoEmail(): void {
    const p = this.emailParticipant; if (!p) return;
    const subject = encodeURIComponent('Your FourSight Thinking Profile');
    const body = encodeURIComponent(this.emailPlainBody(p));
    window.open(`mailto:${p.email}?subject=${subject}&body=${body}`, '_blank');
  }

  // Edit participant modal
  showEditPModal = false;
  editP: Participant | null = null;
  epFirst = '';
  epLast = '';
  epEmail = '';

  editParticipant(p: Participant): void {
    this.editP = p;
    this.epFirst = p.firstName;
    this.epLast = p.lastName;
    this.epEmail = p.email;
    this.showEditPModal = true;
  }
  closeEditP(): void { this.showEditPModal = false; this.editP = null; }
  saveEditP(): void {
    if (!this.editP || !this.epEmail) { return; }
    this.svc.updateParticipant(this.inv!.id, { ...this.editP, firstName: this.epFirst, lastName: this.epLast, email: this.epEmail })
      .subscribe(u => { this.inv = u; this.showEditPModal = false; this.editP = null; });
  }

  toggleFollowup(p: Participant): void {
    this.svc.updateParticipant(this.inv!.id, { ...p, followupDisabled: !p.followupDisabled }).subscribe(u => {
      this.inv = u;
      const disabled = !p.followupDisabled;
      Swal.fire({ title: `Follow-up ${disabled ? 'disabled' : 'enabled'} for ${p.firstName}`, icon: 'success', timer: 1100, showConfirmButton: false });
    });
  }

  thinkingCoach(p: Participant): void {
    Swal.fire({
      title: 'Thinking Coach', text: `Open the FourSight Thinking Coach for ${p.firstName} ${p.lastName}.`,
      icon: 'info', confirmButtonText: 'Open', confirmButtonColor: '#1ab394',
    });
  }

  // Reminders / Messages table
  messageRows(): { key: MessageKey; name: string; updated: string; lastSent?: string; lastSentBy?: string }[] {
    const u = this.inv?.updatedAt ?? '';
    const c = this.inv?.createdAt ?? '';
    const owner = this.inv?.organizers[0]?.name;
    return [
      { key: 'participant_invitation', name: 'Participant invitation', updated: u, lastSent: c, lastSentBy: owner },
      { key: 'participant_reminder', name: 'Participant reminder', updated: u },
      { key: 'thinking_tips', name: 'Thinking tips', updated: u },
    ];
  }

  // Message edit + preview modal (Reminders & Messages table)
  showMsgModal = false;
  msgKey: MessageKey | null = null;
  msgName = '';
  msgSubject = '';
  msgBody = '';
  msgMode: 'edit' | 'preview' = 'edit';
  msgTried = false;

  openMessage(row: { key: MessageKey; name: string }): void {
    if (!this.inv) { return; }
    const tpl = this.inv.messages?.[row.key]
      ?? (row.key === 'participant_invitation' ? this.inv.email : MESSAGE_TEMPLATES[row.key]);
    this.msgKey = row.key;
    this.msgName = row.name;
    this.msgSubject = tpl.subject;
    this.msgBody = tpl.body;
    this.msgMode = 'edit';
    this.msgTried = false;
    this.showMsgModal = true;
  }
  closeMsgModal(): void { this.showMsgModal = false; this.msgKey = null; }

  /** Body with the mock tokens resolved to this invitation's values (used by the Preview tab). */
  msgPreview(): string {
    const fmt = (name: string) => {
      const d = this.inv?.schedule.find(s => s.name === name)?.date;
      return d ? new Date(d).toLocaleDateString('en-US') : '—';
    };
    return this.msgBody
      .replace(/\{\{assessment_link\}\}/g, this.assessmentLink())
      .replace(/\{\{deadline\}\}/g, fmt('deadline'))
      .replace(/\{\{presentation\}\}/g, fmt('presentation'));
  }

  saveMessage(): void {
    if (!this.inv || !this.msgKey) { return; }
    this.msgTried = true;
    if (!this.msgSubject.trim() || !this.msgBody.trim()) { return; }
    const tpl = { subject: this.msgSubject.trim(), body: this.msgBody };
    const patch: Partial<Invitation> = this.msgKey === 'participant_invitation'
      ? { email: tpl }
      : { messages: { ...(this.inv.messages ?? {}), [this.msgKey]: tpl } };
    this.svc.update(this.inv.id, patch).subscribe(inv => {
      this.inv = inv;
      this.closeMsgModal();
      Swal.fire({ title: 'Message saved', icon: 'success', timer: 1100, showConfirmButton: false });
    });
  }

  // The FourSight assessment is 36 statements (9 per preference) — drives the "N/36" progress the
  // Profile column shows for in-progress participants. See assessment.seed.ts.
  readonly totalQuestions = 36;

  // A scored profile is locked until a scoring key has been spent to reveal it (p.unlocked).
  isLocked(p: Participant): boolean { return p.state === 'scored' && !p.unlocked; }
  scoreCell(p: Participant, key: 'cl' | 'id' | 'dv' | 'im'): string | number {
    return (p.scores && !this.isLocked(p)) ? p.scores[key] : '—';
  }

  // row selection / bulk actions
  pToggle(p: Participant): void { this.pSelected.has(p.id) ? this.pSelected.delete(p.id) : this.pSelected.add(p.id); }
  pAllSelected(): boolean { const page = this.pPaged(); return page.length > 0 && page.every(p => this.pSelected.has(p.id)); }
  pToggleAll(): void {
    const page = this.pPaged();
    const allSel = this.pAllSelected();
    page.forEach(p => allSel ? this.pSelected.delete(p.id) : this.pSelected.add(p.id));
  }
  pClearSel(): void { this.pSelected.clear(); }

  // Unlock locked profiles — spend scoring keys (1 per profile). One bulk-bar button covers
  // both scopes: with a selection it targets the locked rows in it, with none it targets every
  // locked profile. A selection holding nothing locked hides the button rather than falling
  // through to "all" — that would spend keys on people the user did not pick.
  lockedInSelection(): Participant[] {
    return (this.inv?.participants ?? []).filter(p => this.pSelected.has(p.id) && this.isLocked(p));
  }
  allLocked(): Participant[] { return (this.inv?.participants ?? []).filter(p => this.isLocked(p)); }
  unlockCost(): number { return this.lockedInSelection().length; }
  allLockedCount(): number { return this.allLocked().length; }

  unlockTargets(): Participant[] { return this.pSelected.size ? this.lockedInSelection() : this.allLocked(); }
  canUnlock(): boolean { return this.unlockTargets().length > 0; }
  unlockLabel(): string {
    return this.pSelected.size ? `Unlock ${this.unlockCost()}` : `Unlock all ${this.allLockedCount()}`;
  }
  openUnlock(): void { this.openUnlockModal(this.unlockTargets()); }

  // Snapshot of the profiles the open modal will unlock, so its counts don't shift under it.
  unlockTargetIds: string[] = [];
  get unlockTargetCount(): number { return this.unlockTargetIds.length; }
  get unlockAvailable(): number { return this.inv?.scoringKeys.available ?? 0; }
  get unlockShortfall(): number { return Math.max(0, this.unlockTargetCount - this.unlockAvailable); }

  private openUnlockModal(targets: Participant[]): void {
    if (!targets.length) { return; }
    this.unlockTargetIds = targets.map(p => p.id);
    this.showUnlockModal = true;
  }
  closeUnlockModal(): void { this.showUnlockModal = false; }
  doUnlock(): void {
    const ids = this.unlockTargetIds;
    if (!ids.length || this.unlockShortfall > 0) { return; }
    this.svc.unlockParticipants(this.inv!.id, ids).subscribe(u => {
      this.inv = u;
      this.showUnlockModal = false;
      this.pSelected.clear();
      Swal.fire({ title: `${ids.length} profile${ids.length === 1 ? '' : 's'} unlocked`, icon: 'success', timer: 1400, showConfirmButton: false });
    });
  }
  /** Not enough keys — hand off to the existing keys-transfer modal. */
  goGetMoreKeys(): void { this.showUnlockModal = false; this.openKeysModal(); }

  remindSelected(): void {
    const n = this.pSelected.size;
    this.pSelected.clear();
    Swal.fire({ title: `Reminder sent to ${n} participant${n === 1 ? '' : 's'}`, icon: 'success', timer: 1300, showConfirmButton: false });
  }

  // "Get or send results" modal
  showResultsModal = false;
  resultsLanguage = 'English';
  resultsMode: 'download_self' | 'email_self' | 'participant_basic' | 'participant_slideshow' = 'download_self';
  resultsSpamTip = false;
  resultLanguages = ['English', 'Spanish', 'French', 'German', 'Dutch', 'Portuguese'];

  // Results emails always go out from the FourSight sender, not the invitation owner.
  get emailOwnerEmail(): string { return 'alex@foursightonline.com'; }

  copySender(): void {
    navigator.clipboard?.writeText(this.emailOwnerEmail);
    Swal.fire({ title: 'Email address copied', icon: 'success', timer: 1100, showConfirmButton: false });
  }
  resultsScoredTotal(): number { return this.scoredCount(); }

  /**
   * The selection-driven actions stay clickable with nothing selected — a greyed-out button
   * explains nothing, whereas clicking one can say exactly what's missing. Returns false (and
   * says so) when the selection is empty.
   */
  private requireSelection(action: string): boolean {
    if (this.pSelected.size) { return true; }
    Swal.fire({
      title: 'Select participants first',
      text: `Choose at least one participant to ${action}.`,
      icon: 'info', confirmButtonText: 'Got it', confirmButtonColor: '#69be28',
    });
    return false;
  }

  getOrSendResults(): void {
    if (!this.requireSelection('get or send results for')) { return; }
    this.resultsMode = 'download_self';
    this.resultsLanguage = this.inv?.language || 'English';
    this.resultsSpamTip = false;
    this.showResultsModal = true;
  }
  closeResultsModal(): void { this.showResultsModal = false; }
  deliverResults(): void {
    const labels: Record<typeof this.resultsMode, string> = {
      download_self: 'Downloading selected profiles (PDF)',
      email_self: 'Emailing you a link to the selected profiles',
      participant_basic: 'Sending profile + interpretive guide to participants',
      participant_slideshow: 'Sending profile + interpretive guide + slideshow to participants',
    };
    const n = this.pSelected.size;
    this.showResultsModal = false;
    Swal.fire({
      title: 'Results delivered',
      text: `${labels[this.resultsMode]} for ${n} participant${n === 1 ? '' : 's'} (${this.resultsLanguage}).`,
      icon: 'success', timer: 1700, showConfirmButton: false,
    });
  }

  createGroupProfile(): void {
    if (!this.requireSelection('build a group profile from')) { return; }
    const n = this.pSelected.size;
    Swal.fire({
      title: 'Create group profile',
      text: `Build a combined group profile from the ${n} selected participant${n === 1 ? '' : 's'}.`,
      icon: 'info', confirmButtonText: 'Create', confirmButtonColor: '#69be28',
    });
  }

  // Downloads (same as Roster / single profile view), grouped under one "Download" dropdown
  pickDownload(choice: 'guides' | 'csv'): void {
    this.dlOpen = false;
    if (choice === 'guides') { this.openGuides(); } else { this.downloadParticipantsCsv(); }
  }

  /** Open the shared FourSight PDF Guides popup — identical to the single profile view. */
  openGuides(): void { $('.guide-modal').css('display', 'block'); }

  /** Export the participants grid to CSV via Blob download — mirrors the Rosters CSV export. */
  downloadParticipantsCsv(): void {
    const esc = (cell: string | number) => `"${String(cell ?? '').replace(/"/g, '""')}"`;
    const header = ['First Name', 'Last Name', 'Email', 'State', 'Profile', 'cl', 'id', 'dv', 'im', 'Completed'];
    const rows = [header.map(esc).join(',')];
    (this.inv?.participants ?? []).forEach(p => {
      rows.push([
        esc(p.firstName),
        esc(p.lastName),
        esc(p.email),
        esc(p.state.replace('_', ' ')),
        esc(this.isLocked(p) ? 'Locked' : (p.profileType || '')),
        esc(this.scoreCell(p, 'cl')),
        esc(this.scoreCell(p, 'id')),
        esc(this.scoreCell(p, 'dv')),
        esc(this.scoreCell(p, 'im')),
        esc(p.lastActivity ? new Date(p.lastActivity).toLocaleDateString() : ''),
      ].join(','));
    });
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(this.inv?.name || 'invitation').replace(/[^\w-]+/g, '_')}_participants.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  whoInvitesLabel(): string { return WHO_INVITES_LABELS[this.inv!.whoInvites]; }
  whoPaysLabel(): string { return WHO_PAYS_LABELS[this.inv!.whoPays]; }
  howResultsLabel(): string { return HOW_RESULTS_LABELS[this.inv!.howResults]; }

  openKeysModal(): void {
    this.keysCount = null;
    this.keysFrom = null;
    this.svc.getRosters().subscribe(r => this.rosters = r);
    this.showKeysModal = true;
  }
  closeKeysModal(): void { this.showKeysModal = false; }

  /** Source organizations for the combobox. The balance rides in the label — it's what you pick on. */
  get rosterOptions(): FsComboOption[] {
    return this.rosters.map(r => ({ id: r.id, name: `${r.name} — ${r.availableKeys} available` }));
  }

  /** Keys the chosen source organization can actually part with. */
  get keysAvailableFrom(): number {
    return this.rosters.find(r => r.id === this.keysFrom)?.availableKeys ?? 0;
  }

  /**
   * `transferKeys` silently clamps a too-large request to the source's balance, so asking for 50
   * from an org holding 6 quietly moves 6. Say so instead of moving a different number than the
   * one on screen.
   */
  get keysCountError(): string {
    const n = this.keysCount;
    if (n === null || n === undefined) { return ''; }
    if (!Number.isInteger(n) || n < 1) { return 'Enter a whole number of keys, 1 or more.'; }
    if (this.keysFrom && n > this.keysAvailableFrom) {
      return `That organization has only ${this.keysAvailableFrom} key${this.keysAvailableFrom === 1 ? '' : 's'} available.`;
    }
    return '';
  }

  get canTransfer(): boolean { return !!this.keysCount && !!this.keysFrom && !this.keysCountError; }

  doTransfer(): void {
    if (!this.canTransfer) { return; }
    this.svc.transferKeys(this.inv!.id, this.keysFrom!, this.keysCount!).subscribe(u => {
      this.inv = u;
      this.showKeysModal = false;
      Swal.fire({ title: 'Keys transferred', icon: 'success', timer: 1300, showConfirmButton: false });
    });
  }

  purchaseAssessments(): void {
    Swal.fire({
      title: 'Purchase FourSight Assessments',
      text: 'This would open the FourSight store to buy assessment keys.',
      icon: 'info', confirmButtonText: 'Got it', confirmButtonColor: '#1ab394',
    });
  }

  constructor(
    private svc: InvitationMockService,
    private sessionSvc: SessionMockService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void { this.load(); }

  private load(): void {
    const id = this.route.snapshot.paramMap.get('id')!;
    this.svc.getById(id).subscribe(inv => {
      if (!inv) { this.router.navigate(['/invitations']); return; }
      this.inv = inv;
      this.loadActivityGroup();
    });
  }

  // ─────────────────────────────────────────────
  // Activity Groups — a "Create groups" CTA when none exists, or the full editable board inline
  // once created. Mirrors SessionBoardPageComponent's lifecycle (`session`→`activityGroup`,
  // `members`→`activityMembers`), driven by SessionMockService against this one invitation.
  // ─────────────────────────────────────────────
  activityGroup: Session | null = null;
  /** Resolved, in participantIds order — the board never looks ids up itself. */
  activityMembers: Participant[] = [];
  /** An array, not a Set — Angular does not track Set mutations, and the board takes it as @Input. */
  activitySelected: string[] = [];
  showActivityBuilder = false;
  activityDlOpen = false;
  activityDownload: FsReportTarget | null = null;

  private loadActivityGroup(): void {
    this.sessionSvc.forInvitation(this.inv!.id).subscribe(s => this.setActivityGroup(s ?? null));
  }
  private setActivityGroup(s: Session | null): void {
    this.activityGroup = s;
    this.activityMembers = s ? this.svc.participantsByIds(s.participantIds) : [];
    // Drop selections for anyone no longer resolvable (e.g. a group was deleted, or recreated).
    this.activitySelected = this.activitySelected.filter(id => this.activityMembers.some(m => m.id === id));
    // Same for group selection: recreating mints new group ids, so stale ones must not survive.
    this.activitySelectedGroups = this.activitySelectedGroups.filter(
      id => (s?.groups ?? []).some(g => g.id === id));
  }
  onGroupsCreated(s: Session): void { this.showActivityBuilder = false; this.setActivityGroup(s); }

  private applyActivity(obs: Observable<Session>): void { obs.subscribe(s => this.setActivityGroup(s)); }

  get activityExpired(): boolean { return !!this.activityGroup && this.sessionSvc.isExpired(this.activityGroup); }
  activitySplitLabel(): string { return this.activityGroup ? SPLIT_TYPE_LABELS[this.activityGroup.splitType] : ''; }
  activityEligibleCount(): number { return this.inv ? this.svc.eligibleParticipants(this.inv).length : 0; }
  /**
   * Everyone who could be grouped, can be. Requires at least one participant: an empty invitation
   * satisfies `eligible === total` arithmetically, but "0 of 0 ready" would invite someone to
   * build groups out of nobody.
   */
  activityAllEligible(): boolean {
    const total = this.inv?.participants.length ?? 0;
    return total > 0 && this.activityEligibleCount() === total;
  }

  // --- board wiring ---
  onActivityMove(e: { memberId: string; toGroupId: string; toIndex: number }): void {
    if (!this.activityGroup) { return; }
    this.applyActivity(this.sessionSvc.moveMember(this.activityGroup.id, e.memberId, e.toGroupId, e.toIndex));
  }
  onActivityRenameGroup(e: { groupId: string; name: string }): void {
    if (!this.activityGroup) { return; }
    this.applyActivity(this.sessionSvc.renameGroup(this.activityGroup.id, e.groupId, e.name));
  }
  onActivityDeleteGroup(groupId: string): void {
    if (!this.activityGroup) { return; }
    this.applyActivity(this.sessionSvc.deleteGroup(this.activityGroup.id, groupId));
  }
  addActivityGroup(): void {
    if (!this.activityGroup) { return; }
    this.applyActivity(this.sessionSvc.addGroup(this.activityGroup.id));
  }

  // --- selection ---
  toggleActivityMember(id: string): void {
    this.activitySelected = this.activitySelected.includes(id)
      ? this.activitySelected.filter(x => x !== id)
      : [...this.activitySelected, id];
  }
  selectActivityGroup(groupId: string): void {
    const ids = this.activityGroup?.groups.find(g => g.id === groupId)?.memberIds ?? [];
    this.activitySelected = [...new Set([...this.activitySelected, ...ids])];
  }
  clearActivitySelection(): void { this.activitySelected = []; }

  /** Options for the selection bar's "Move to group" keyboard control. */
  get activityGroupOptions(): FsComboOption[] {
    return this.activityGroup?.groups.map(g => ({ id: g.id, name: g.name })) ?? [];
  }

  /**
   * Keyboard-accessible path for moving the selected members between groups (drag has no keyboard
   * equivalent). Mirrors the standalone board: N sequential moveMember calls are safe because
   * SessionMockService.mutate() is synchronous — each subscribe runs before the next iteration.
   */
  moveActivitySelectionToGroup(toGroupId: string | null): void {
    if (!toGroupId || !this.activitySelected.length || !this.activityGroup) { return; }
    const id = this.activityGroup.id;
    let last: Session | undefined;
    this.activitySelected.forEach(memberId => {
      this.sessionSvc.moveMember(id, memberId, toGroupId, 0).subscribe(s => { last = s; });
    });
    this.clearActivitySelection();
    if (last) { this.setActivityGroup(last); }
  }

  downloadActivitySelection(): void {
    this.activityDownload = {
      title: `${this.activitySelected.length} selected`,
      members: this.toActivityReportMembers(this.activitySelected),
      live: false,
      allowCsv: true,
    };
  }

  /**
   * Recreating doesn't edit the existing activity group — `createForInvitation` drops it and
   * builds a new one, so the current groups and any hand-arranged members are gone for good.
   * Confirm before opening the builder rather than after, so a misclick costs nothing.
   */
  confirmRecreateGroups(): void {
    Swal.fire({
      title: 'Recreate these groups?',
      text: 'The current groups will be deleted, including any members you moved by hand. This cannot be undone.',
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Recreate', confirmButtonColor: '#69be28',
    }).then(r => {
      if (r.isConfirmed) { this.showActivityBuilder = true; }
    });
  }

  deleteActivityGroup(): void {
    if (!this.activityGroup) { return; }
    const id = this.activityGroup.id;
    Swal.fire({
      title: 'Delete this activity group?',
      text: 'This permanently removes the activity group and its groups. This cannot be undone.',
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#f54359',
    }).then(r => {
      if (r.isConfirmed) { this.sessionSvc.remove(id).subscribe(() => this.setActivityGroup(null)); }
    });
  }

  // --- downloads ---
  private toActivityReportMembers(ids: string[]): FsReportMember[] {
    return ids.map((id): FsReportMember | undefined => {
      const p = this.activityMembers.find(x => x.id === id);
      const group = this.activityGroup?.groups.find(g => g.memberIds.includes(id));
      return p && {
        id: p.id, firstName: p.firstName, lastName: p.lastName,
        groupName: group?.name, profileTypeId: p.profileTypeId,
      };
    }).filter((m): m is FsReportMember => !!m);
  }
  downloadActivitySession(): void {
    this.activityDlOpen = false;
    const ids = this.activityGroup?.groups.flatMap(g => g.memberIds) ?? [];
    this.activityDownload = { title: this.activityGroup?.name ?? 'Activity group', members: this.toActivityReportMembers(ids), live: false, allowCsv: true };
  }
  downloadActivityGroup(groupId: string): void {
    const g = this.activityGroup?.groups.find(x => x.id === groupId);
    if (!g) { return; }
    this.activityDownload = { title: g.name, members: this.toActivityReportMembers(g.memberIds), live: false, allowCsv: true };
  }

  // --- group-level selection ---
  // A separate axis from `activitySelected`, which selects participants: groups are picked to act
  // on whole groups, participants to act on people. Each bar clears only its own axis.
  activitySelectedGroups: string[] = [];

  toggleActivityGroupSelect(groupId: string): void {
    this.activitySelectedGroups = this.activitySelectedGroups.includes(groupId)
      ? this.activitySelectedGroups.filter(id => id !== groupId)
      : [...this.activitySelectedGroups, groupId];
  }
  clearActivityGroupSelection(): void { this.activitySelectedGroups = []; }

  /**
   * Download the selected groups' reports. The report target takes a flat member list, so this
   * mirrors the single-group action with a title naming the groups — producing genuinely
   * per-group documents is the reporting API's job, not something the mock can fake.
   */
  downloadActivitySelectedGroups(): void {
    const groups = (this.activityGroup?.groups ?? []).filter(g => this.activitySelectedGroups.includes(g.id));
    if (!groups.length) { return; }
    const memberIds = [...new Set(groups.flatMap(g => g.memberIds))];
    this.activityDownload = {
      title: groups.length === 1 ? groups[0].name : `${groups.length} groups`,
      members: this.toActivityReportMembers(memberIds),
      live: false,
      // Group-scoped, so the CSV is a real group list: every member carries its group name.
      allowCsv: true,
    };
  }

  // A participant's stage: not started → started → scored, where a completed (scored)
  // profile with no available key is "locked" until a key is assigned.
  stageKey(p: Participant): 'not_started' | 'started' | 'locked' | 'scored' {
    if (p.state === 'scored') { return this.isLocked(p) ? 'locked' : 'scored'; }
    return p.state;
  }
  stageLabel(p: Participant): string {
    return { not_started: 'Not started', started: 'Started', locked: 'Locked', scored: 'Scored' }[this.stageKey(p)];
  }

  scoredCount(): number { return this.inv!.participants.filter(p => p.state === 'scored').length; }   // all completed (incl. locked)
  unlockedCount(): number { return this.inv!.participants.filter(p => this.stageKey(p) === 'scored').length; }
  lockedCount(): number { return this.inv!.participants.filter(p => this.stageKey(p) === 'locked').length; }
  startedCount(): number { return this.inv!.participants.filter(p => p.state === 'started').length; }
  notStartedCount(): number { return this.inv!.participants.filter(p => p.state === 'not_started').length; }

  /** Open the mail client with a reminder email addressed to this participant. */
  /** Participant-facing assessment link for this invitation. */
  assessmentLink(): string { return `${location.origin}/assessments/${this.inv?.id}`; }

  remindParticipant(p: Participant): void {
    const link = this.assessmentLink();
    const dl = this.presentation() || this.deadline();
    const dlStr = dl ? new Date(dl).toLocaleDateString('en-US') : '';
    const subject = encodeURIComponent('Reminder: Complete your FourSight Assessment');
    const body = encodeURIComponent(
      `Dear ${p.firstName},\n\n` +
      `This is a friendly reminder to finish your FourSight Thinking Profile assessment — it takes about 10 minutes.\n\n` +
      `Take it here:\n  ${link}\n` +
      (dlStr ? `\nDeadline: Complete the assessment by  ${dlStr}\n` : '') +
      `\nThank you!`);
    window.open(`mailto:${p.email}?subject=${subject}&body=${body}`, '_blank');
  }
  removeParticipant(p: Participant): void {
    Swal.fire({
      title: `Remove ${p.firstName} ${p.lastName}?`, text: 'They will be removed from this invitation.',
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Remove', confirmButtonColor: '#f54359',
    }).then(r => {
      if (r.isConfirmed) { this.svc.removeParticipant(this.inv!.id, p.id).subscribe(u => { this.inv = u; this.pSelected.delete(p.id); }); }
    });
  }
  deadline(): string { return this.inv!.schedule.find(s => s.name === 'deadline')?.date ?? ''; }
  presentation(): string { return this.inv!.schedule.find(s => s.name === 'presentation')?.date ?? ''; }
  initials(name: string): string { return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase(); }

  changeStatus(status: InvitationStatus): void {
    this.svc.updateStatus(this.inv!.id, status).subscribe(u => this.inv = u);
  }
  edit(): void { this.showEditModal = true; }
  closeEdit(): void { this.showEditModal = false; }
  onEdited(): void { this.showEditModal = false; this.load(); }

  archive(): void {
    Swal.fire({ title: `Archive "${this.inv!.name}"?`, icon: 'warning', showCancelButton: true, confirmButtonText: 'Archive' })
      .then(r => { if (r.isConfirmed) this.svc.archive(this.inv!.id, true).subscribe(() => this.router.navigate(['/invitations'])); });
  }

  // "Invite more" — 2-step participant invitation email wizard (Edit Message → Send)
  showInviteWizard = false;
  inviteStep = 1;
  inviteSubject = '';
  inviteBody = '';
  inviteBcc = 'invites@foursightonline.com';

  inviteMore(): void {
    const link = `${location.origin}/assessments/${this.inv!.id}`;
    const dl = this.presentation() || this.deadline();
    this.inviteSubject = 'TO DO: Take FourSight Assessment';
    this.inviteBody = this.defaultInviteBody(link, dl);
    this.inviteStep = 1;
    this.showInviteWizard = true;
  }
  closeInviteWizard(): void { this.showInviteWizard = false; }
  inviteNext(): void { this.inviteStep = 2; }
  inviteBack(): void { this.inviteStep = 1; }

  /** Open the user's mail client with bcc/subject/body pre-filled. */
  generateInviteEmail(): void {
    const subject = encodeURIComponent(this.inviteSubject);
    const body = encodeURIComponent(this.inviteBody);
    const bcc = encodeURIComponent(this.inviteBcc);
    window.open(`mailto:?bcc=${bcc}&subject=${subject}&body=${body}`, '_blank');
  }

  copyText(text: string): void {
    navigator.clipboard?.writeText(text).then(
      () => Swal.fire({ title: 'Copied', icon: 'success', timer: 900, showConfirmButton: false }),
      () => { /* clipboard unavailable — user can select manually */ },
    );
  }

  finishInvite(): void {
    this.showInviteWizard = false;
    Swal.fire({
      title: 'Invitation sent', text: 'Participants will appear here as they complete the assessment.',
      icon: 'success', timer: 1700, showConfirmButton: false,
    });
  }

  private defaultInviteBody(link: string, deadline: string): string {
    const dl = deadline ? new Date(deadline).toLocaleDateString('en-US') : '';
    return `Dear Participant,

In our upcoming session, we will be reviewing the results of the "FourSight Thinking Profile." FourSight is a multi-item assessment that reveals where your energies lie in the problem-solving process. Every profile contributes in some way.

Please read the directions and answer all the questions. Allow about 10 minutes to complete the assessment.

Once you respond to all the items, your job is done. Your answers will automatically go to a secure database. You will get your results at our upcoming session.

Follow the link below to take FourSight online.

  ${link}

Deadline: Complete the assessment by  ${dl}

Thank you!`;
  }

  // Add organizer modal
  showOrgModal = false;
  orgName = '';
  orgEmail = '';

  addOrganizer(): void { this.orgName = ''; this.orgEmail = ''; this.showOrgModal = true; }
  closeOrgModal(): void { this.showOrgModal = false; }
  saveOrganizer(): void {
    if (!this.orgName || !this.orgEmail) { return; }
    // Newly invited organizers haven't approved yet → pending until they accept.
    this.svc.addOrganizer(this.inv!.id, { name: this.orgName, email: this.orgEmail, role: 'presenter' as OrganizerRole, notify: false, accepted: false })
      .subscribe(u => { this.inv = u; this.showOrgModal = false; });
  }

  /** An organizer who was invited but hasn't approved the invitation yet. */
  organizerPending(o: Organizer): boolean { return o.accepted === false; }

  /** Send a reminder to a pending organizer to approve their invitation. */
  remindOrganizer(o: Organizer): void {
    Swal.fire({ title: `Reminder sent to ${o.name}`, text: 'They\'ll be asked to approve the invitation.', icon: 'success', timer: 1300, showConfirmButton: false });
  }

  removeOrganizer(id: string): void {
    const o = this.inv?.organizers.find(x => x.id === id);
    Swal.fire({
      title: `Remove ${o?.name || 'organizer'}?`,
      text: 'They will no longer be an organizer on this invitation.',
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Remove', confirmButtonColor: '#f54359',
    }).then(r => {
      if (r.isConfirmed) { this.svc.removeOrganizer(this.inv!.id, id).subscribe(u => this.inv = u); }
    });
  }
}
